import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { Role } from '../../../domain/usecases/Roles'
import { DataScopeSelector } from './DataScopeSelector'
import { RolePermissionsDisplay } from './RolePermissionsDisplay'
import { PermissionsSelector } from './PermissionsSelector'
import { RemoteAssignUserRole } from '../../../data/usecases/UserRoles/RemoteAssignUserRole'
import { RemoteCreateCustomRole } from '../../../data/usecases/Roles/RemoteCreateCustomRole'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'
import { throwIfHttpError } from '../httpError'

interface InviteUserModalProps {
  show: boolean
  onHide: () => void
  onSuccess: () => void
  roles: Role[]
  companyId: string
}

interface FormData {
  user_email: string
  role_id: string
  custom_role_name: string
  scope_type: 'full' | 'restricted'
  scope_resources: string[]
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({
  show,
  onHide,
  onSuccess,
  roles,
  companyId
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      scope_type: 'full',
      scope_resources: []
    }
  })
  const httpClient = useAxiosHttpClient()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [customPermissions, setCustomPermissions] = useState<string[]>([])

  const scopeType = watch('scope_type')
  const selectedRoleId = watch('role_id')

  // Determine if selected role is system role or custom
  const selectedRole = roles.find((r) => r.id === selectedRoleId)
  const isSystemRole = selectedRole ? 'is_system' in selectedRole : false
  const isCreatingCustomRole = selectedRoleId === 'custom'

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    setError(null)

    try {
      let roleIdToAssign = data.role_id

      // If creating a custom role, create it first
      if (isCreatingCustomRole) {
        if (!data.custom_role_name || data.custom_role_name.trim() === '') {
          setError('Por favor, insira um nome para a função personalizada')
          setLoading(false)
          return
        }

        if (customPermissions.length === 0) {
          setError('Por favor, selecione pelo menos uma permissão')
          setLoading(false)
          return
        }

        const createRoleUseCase = new RemoteCreateCustomRole(
          URL_API || '',
          httpClient
        )

        const newRole = await createRoleUseCase.create({
          name: data.custom_role_name
            .toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, ''),
          display_name: data.custom_role_name,
          description: `Função personalizada com ${customPermissions.length} permissões`,
          company_id: companyId,
          permissions: customPermissions
        })

        throwIfHttpError(newRole, 'Erro ao criar função personalizada')
        roleIdToAssign = newRole.id
      }

      const assignRoleUseCase = new RemoteAssignUserRole(
        URL_API || '',
        httpClient
      )

      const assignment = await assignRoleUseCase.assign({
        user_email: data.user_email,
        company_id: companyId,
        role_id: roleIdToAssign,
        is_system_role: !isCreatingCustomRole && isSystemRole,
        scope_type: data.scope_type,
        scope_entity: data.scope_type === 'restricted' ? 'spe' : undefined,
        scope_resources:
          data.scope_type === 'restricted' ? data.scope_resources : undefined
      })
      throwIfHttpError(assignment, 'Erro ao convidar usuário')

      onSuccess()
    } catch (err: any) {
      console.error('Error inviting user:', err)
      setError(err.message || 'Erro ao convidar usuário')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Convidar Usuário</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form.Group>
            <Form.Label>E-mail do Usuário *</Form.Label>
            <Form.Control
              type="email"
              placeholder="usuario@email.com"
              {...register('user_email', {
                required: 'E-mail é obrigatório',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'E-mail inválido'
                }
              })}
              isInvalid={!!errors.user_email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.user_email?.message}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Se o usuário não existir, um convite será enviado.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>Função *</Form.Label>
            <Form.Control
              as="select"
              {...register('role_id', { required: 'Função é obrigatória' })}
              isInvalid={!!errors.role_id}
            >
              <option value="">Selecione uma função...</option>
              <optgroup label="Funções do Sistema">
                {roles
                  .filter((r) => 'is_system' in r && r.is_system)
                  .map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.display_name}
                    </option>
                  ))}
              </optgroup>
              {roles.filter((r) => !('is_system' in r) || !r.is_system).length >
                0 && (
                <optgroup label="Funções Personalizadas">
                  {roles
                    .filter((r) => !('is_system' in r) || !r.is_system)
                    .map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.display_name}
                      </option>
                    ))}
                </optgroup>
              )}
              <optgroup label="Criar Nova">
                <option value="custom">+ Criar Função Personalizada</option>
              </optgroup>
            </Form.Control>
            <Form.Control.Feedback type="invalid">
              {errors.role_id?.message}
            </Form.Control.Feedback>
          </Form.Group>

          {isCreatingCustomRole && (
            <>
              <Form.Group>
                <Form.Label>Nome da Função Personalizada *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Gerente de Vendas"
                  {...register('custom_role_name', {
                    required: isCreatingCustomRole
                      ? 'Nome da função é obrigatório'
                      : false
                  })}
                  isInvalid={!!errors.custom_role_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.custom_role_name?.message}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Este nome será usado para identificar esta função no futuro
                </Form.Text>
              </Form.Group>

              <PermissionsSelector
                selectedPermissions={customPermissions}
                onChange={setCustomPermissions}
              />
            </>
          )}

          {selectedRoleId && !isCreatingCustomRole && (
            <RolePermissionsDisplay
              roleId={selectedRoleId}
              isSystemRole={isSystemRole}
            />
          )}

          <Form.Group>
            <Form.Label>Escopo de Acesso *</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="scope-full"
                label="Acesso Completo (Todas as SPEs)"
                value="full"
                {...register('scope_type')}
                defaultChecked
              />
              <Form.Check
                type="radio"
                id="scope-restricted"
                label="Acesso Restrito (Selecionar SPEs específicas)"
                value="restricted"
                {...register('scope_type')}
              />
            </div>
          </Form.Group>

          {scopeType === 'restricted' && (
            <DataScopeSelector
              companyId={companyId}
              selectedResources={watch('scope_resources') || []}
              onChange={(resources) => setValue('scope_resources', resources)}
            />
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Convidando...
              </>
            ) : (
              <>
                <i className="feather icon-send mr-2" />
                Enviar Convite
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
